import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Grid, Row, Stack, Text } from 'rsuite';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { RegistrantRadioGroup } from '../RegistrantRadioGroup';
import { ExpressRegistrationRegistrant } from 'src/types/configuration';
import { OwnersForm, OwnersFormRef } from '../Owners';
import FormFieldErrorIcon from '../FormFieldErrorIcon';
import { FormModelPerson, Person } from 'src/types/person';
import { mapFormModelToPerson, mapOwnersToFormModel } from 'src/common/people';
import { useUserRole } from 'src/hooks/useUserRole';

interface RegistrantFormProps {
  data: ApplicationData;
  multipleOwners?: boolean;
  registrantOptions: ExpressRegistrationRegistrant;
  isAdminUpdate?: boolean;
}

export interface RegistrantFormRef {
  getValues: () => ApplicationData;
  validateForm: () => boolean;
}

export const RegistrantForm = forwardRef<
  RegistrantFormRef,
  RegistrantFormProps
>(
  (
    {
      data,
      multipleOwners,
      registrantOptions,
      isAdminUpdate = false,
    }: RegistrantFormProps,
    ref
  ) => {
    const ownersRef = useRef<OwnersFormRef>(null);
    const [owners, setOwners] = useState<FormModelPerson[]>([]);
    const [hasUnselectedOwnerRadioError, setHasUnselectedOwnerRadioError] =
      useState(false);
    const { registrant, contacts, property, tenant } = data;

    const {
      selectedRegistrantOption,
      isPropertyManager,
      isTenant,
      isRegistrant,
      isOwner,
    } = useUserRole(registrant, contacts, tenant);

    useImperativeHandle(ref, () => ({
      validateForm: () => {
        if (!selectedRegistrantOption) {
          setHasUnselectedOwnerRadioError(true);
          return false;
        }
        if (selectedRegistrantOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER) {
          return ownersRef?.current?.validateForm() ?? false;
        }
        return true;
      },
      getValues: () => {
        let permitData: ApplicationData;
        if (selectedRegistrantOption === REGISTRANT_OPTIONS.OWNER) {
          // When owner is selected, no changes can be applied to anything
          permitData = {
            registrant,
          };
        } else {
          permitData = {
            registrant: {
              registrantType: selectedRegistrantOption as REGISTRANT_OPTIONS,
              owner: owners.map(mapFormModelToPerson),
              registrant: { ...(registrant?.registrant as Person) },
            },
          };
        }
        return permitData;
      },
    }));

    useEffect(() => {
      if (isPropertyManager || isTenant) {
        const newOwners = mapOwnersToFormModel(
          registrant?.owner ?? [],
          property?.propertyaddress
        );
        setOwners(newOwners);
      }
    }, [registrant, property, isPropertyManager, isTenant]);

    const canChangePartialOwnerDetails = useMemo(() => {
      return isOwner || isAdminUpdate || (isPropertyManager && isRegistrant);
    }, [isPropertyManager, isRegistrant, isAdminUpdate, isOwner]);

    const canChangeAllOwnerDetails = useMemo(() => {
      return (
        (registrantOptions.allow_owner_editing !== false &&
          isPropertyManager &&
          isRegistrant) ||
        isAdminUpdate
      );
    }, [isPropertyManager, isRegistrant, isAdminUpdate, registrantOptions]);

    return (
      <Grid
        className="mt-12 ml-0"
        fluid
      >
        <Row>
          <Col xs={24}>
            <Stack spacing={5}>
              <Text>Are you the owner of the rental property?</Text>
              {hasUnselectedOwnerRadioError && (
                <FormFieldErrorIcon errorMessage={'Selection is required'} />
              )}
            </Stack>
            <RegistrantRadioGroup
              configuration={registrantOptions}
              value={selectedRegistrantOption}
              disabled
            />
          </Col>
        </Row>
        {selectedRegistrantOption !== REGISTRANT_OPTIONS.OWNER && (
          <OwnersForm
            ref={ownersRef}
            values={owners}
            onChange={setOwners}
            propertyAddress={property?.propertyaddress}
            multipleOwners={multipleOwners}
            disableSimpleEdition={canChangePartialOwnerDetails}
            disabled={!canChangeAllOwnerDetails}
          />
        )}
      </Grid>
    );
  }
);
